/**
 * Who operates ato.run, and where the legal documents live.
 *
 * ato.run is a TRADE NAME (屋号), not a company name. The service is provided
 * by an individual sole proprietor; there is no operating company. This module
 * is the single source of truth for that fact so the footer, the pre-checkout
 * confirmation screens, and the static legal pages can never drift apart —
 * and so a future transfer of the business to a company is a one-file change
 * here plus the MDX under content/legal/.
 *
 * The statutory seller disclosure (販売事業者名 / 運営責任者 / 所在地 / 電話番号)
 * lives ONLY on the 特定商取引法 page (content/legal/tokushoho.mdx). Do not
 * copy the address or phone number into product UI — LEGAL_LINKS.tokushoho is
 * how a buyer reaches it.
 */

/** The service/brand and trade name. Never rendered as a company name. */
export const SERVICE_NAME = "ato.run";

/**
 * The natural person who is the contracting party, the personal-information
 * handler, and the seller of record. Matches content/legal/tokushoho.mdx
 * (販売事業者名 / 運営責任者) — keep the two in step.
 */
export const OPERATOR_LEGAL_NAME = "Kohsuke Egami";

/** The same person as written in Japanese, for JA-facing surfaces. */
export const OPERATOR_LEGAL_NAME_JA = "江上広介";

/** Support / legal / privacy-request mailbox. Also on the contact + 特商法 pages. */
export const OPERATOR_CONTACT_EMAIL = "kohsuke@ato.run";

/**
 * One-line attribution for product UI. Deliberately short: the brand leads and
 * the individual's name is the qualifier, not the headline.
 */
export const OPERATOR_LINE =
  SERVICE_NAME +
  " is a service operated by " +
  OPERATOR_LEGAL_NAME +
  ", an individual sole proprietor in Japan.";

/** Footer copyright — the trade name, matching the legal documents. */
export const COPYRIGHT_HOLDER = SERVICE_NAME;

/**
 * Routes of the statically-rendered legal pages. These must stay in sync with
 * the "route:" frontmatter of content/legal/*.mdx and with the Worker's
 * STATIC_CONTENT_ROUTES allowlist (worker/index.ts) — a route missing from
 * that Set falls through to the SPA shell and 200s with the wrong document.
 */
export const LEGAL_LINKS = {
  terms: "/legal/terms/",
  privacy: "/legal/privacy/",
  security: "/legal/security/",
  acceptableUse: "/legal/acceptable-use/",
  billing: "/legal/billing-policies/",
  ipPolicy: "/legal/ip-policy/",
  tokushoho: "/legal/tokushoho/",
  contact: "/legal/contact/",
} as const;

/**
 * The four documents a buyer must be able to reach BEFORE paying: the contract,
 * the privacy terms, the refund/cancellation terms, and the Japanese statutory
 * seller disclosure. Rendered by <PurchaseTerms> on every pre-checkout
 * screen.
 */
export const PRE_CHECKOUT_LEGAL_LINKS: ReadonlyArray<{
  label: string;
  href: string;
}> = [
  { label: "Terms", href: LEGAL_LINKS.terms },
  { label: "Privacy", href: LEGAL_LINKS.privacy },
  { label: "Billing & refunds", href: LEGAL_LINKS.billing },
  { label: "特定商取引法に基づく表記", href: LEGAL_LINKS.tokushoho },
];
